import { format } from 'date-fns'
import { useEffect, useState } from 'react'

import { Card, CardContent } from '@/components/ui/card'
import { SkeletonRows } from '@/components/ui/skeleton'
import { FillOpeningState, hasFilesReading, useFillApplicationState } from '@/models/fillApplication'
import type { Org } from '@/models/user'
import { DotNumberRow } from './DotNumberRow'
import { FillStep } from './fillSteps'
import { OpeningView } from './OpeningView'
import { useFillApplicationTracking } from './useFillApplicationTracking'
import { useRefreshChecksOnRosterChange } from './useRefreshChecksOnRosterChange'
import { useRefreshOnFileFinished } from './useRefreshOnFileFinished'
import { useRefreshOrgOnOwnerLink } from './useRefreshOrgOnOwnerLink'

/** Keyed by application id where it is rendered, so nothing carries over between customers. */
export function FillApplicationTab({ org }: { org: Org }) {
  const [poll, setPoll] = useState(false)
  const { state, isLoading, isError } = useFillApplicationState(org.id, { poll })
  const reading = hasFilesReading(state)
  // A drop on the documents step opens the form on its files; skipping it opens on Business.
  const [initialStep, setInitialStep] = useState<FillStep | null>(null)

  useEffect(() => {
    setPoll(reading)
  }, [reading])

  useRefreshOnFileFinished(org.id, state)
  useRefreshChecksOnRosterChange(org.id)
  useRefreshOrgOnOwnerLink(org, state)
  useFillApplicationTracking(org.id, state)
  // Set on the FMCSA step, so it shows only once the form is up.
  const dotNumber = state?.openingState === FillOpeningState.FORM ? (org.business?.dotNumber ?? '') : ''

  // A failed refetch keeps the last state: swapping the form out would throw away unsaved edits.
  if (!state) {
    if (isLoading || !isError) return <SkeletonRows rows={6} />
    return (
      <Card>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            Couldn&apos;t load this application. Refresh the page to try again.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {(state.application || dotNumber) && (
        <div className="text-ink-500 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          {state.application && (
            <span>Application started {format(new Date(state.application.created), 'MMM d')}</span>
          )}
          {state.application && dotNumber && <span aria-hidden>·</span>}
          {dotNumber && (
            <DotNumberRow
              orgId={org.id}
              dotNumber={dotNumber}
              locked={state.changeDotLocked}
              sent={state.sent}
            />
          )}
        </div>
      )}
      <OpeningView
        org={org}
        state={state}
        initialStep={initialStep}
        onUploaded={() => setInitialStep(FillStep.DOCUMENTS)}
      />
    </div>
  )
}
